import React, { FC, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { pricingResource, pricingResourceItem } from "../../../api/endpoints";
import { PackageRow, usePricing } from "./PricingContext";
import { DotLottleLoader } from "../../../components/Loader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { LineItemsEditor } from "./LineItemsEditor";

const EMPTY_DRAFT = { key: "", display_name: "", sell_price: "", max_skus: "", term_months: "" };

export const Packages: FC = () => {
    const { data, loading, refetch, canEdit } = usePricing();
    const [busyId, setBusyId] = useState<number | null>(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    // Package ids whose cost sheet is expanded.
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    const toggleExpanded = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (loading || !data) return <DotLottleLoader />;

    const handlePatch = async (id: number, patch: Partial<PackageRow>) => {
        setBusyId(id);
        try {
            await axiosInstant.patch(pricingResourceItem("packages", id), patch);
            toast.success("Saved");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (id: number) => {
        if (
            !window.confirm(
                "Delete this package? SOWs already sold under it keep their snapshotted terms, but it can no longer be attached. Consider unchecking Active instead."
            )
        ) {
            return;
        }
        setBusyId(id);
        try {
            await axiosInstant.delete(pricingResourceItem("packages", id));
            toast.success("Deleted");
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to delete");
        } finally {
            setBusyId(null);
        }
    };

    const handleCreate = async () => {
        if (!draft.key.trim() || !draft.display_name.trim()) {
            toast.error("Key and display name are required");
            return;
        }
        const sellPrice = Number(draft.sell_price);
        if (!Number.isFinite(sellPrice) || sellPrice < 0) {
            toast.error("Sell price must be a non-negative number");
            return;
        }
        const maxSkus = Number(draft.max_skus || 1);
        if (!Number.isInteger(maxSkus) || maxSkus < 1) {
            toast.error("Max SKUs must be a positive integer");
            return;
        }
        try {
            await axiosInstant.post(pricingResource("packages"), {
                key: draft.key.trim(),
                display_name: draft.display_name.trim(),
                sell_price: sellPrice,
                max_skus: maxSkus,
                term_months: draft.term_months === "" ? null : Number(draft.term_months),
                sort_order: data.packages.length
            });
            toast.success("Package added");
            setDraft(EMPTY_DRAFT);
            await refetch();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to add package");
        }
    };

    // Numeric blur-patch helper: nullable fields send null when cleared.
    const patchNumber = (
        row: PackageRow,
        field: keyof PackageRow,
        raw: string,
        opts: { nullable?: boolean; integer?: boolean } = {},
        input?: HTMLInputElement | HTMLTextAreaElement
    ) => {
        if (raw === "") {
            if (opts.nullable && row[field] !== null) {
                handlePatch(row.id, { [field]: null });
            } else if (!opts.nullable && input) {
                // Non-nullable field cleared: no-op — Number("") is 0, and a
                // select-all-retype that blurs mid-edit must not save $0.
                // Restore the display (inputs are uncontrolled defaultValue).
                input.value = String(row[field] ?? "");
            }
            return;
        }
        const v = Number(raw);
        if (opts.integer ? !Number.isInteger(v) || v < 0 : !Number.isFinite(v)) return;
        if (v !== Number(row[field])) handlePatch(row.id, { [field]: v });
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Packages
            </Typography>
            <Box
                sx={{
                    backgroundColor: "var(--tint)",
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                    padding: "12px 16px",
                    mb: 2
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "CircularStd",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "var(--accent-text)",
                        lineHeight: 1.45
                    }}
                >
                    Packages are flat-price engagements attached to a SOW — the sell price is the
                    billed amount for the whole engagement. Terms are snapshotted onto the SOW when a
                    package is attached, so edits here never change already-sold engagements.
                </Typography>
            </Box>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 36 }} />
                            <TableCell>
                                <strong>Key</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Name</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Sell Price</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Max SKUs</strong>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Blank = single project, no term window">
                                    <strong>Term (mo)</strong>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Reference only — the effective per-video rate shown on quotes">
                                    <strong>Per-Video $</strong>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <strong>Payment Schedule</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Deliverables</strong>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Priority scheduling">
                                    <strong>Priority</strong>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <Tooltip title="Unused capacity rolls over">
                                    <strong>Rollover</strong>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <strong>Active</strong>
                            </TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.packages.map((row) => (
                            <React.Fragment key={row.id}>
                            <TableRow sx={row.active ? undefined : { opacity: 0.5 }}>
                                <TableCell sx={{ width: 36 }}>
                                    <IconButton
                                        size="small"
                                        title="Cost sheet"
                                        onClick={() => toggleExpanded(row.id)}
                                    >
                                        {expanded.has(row.id) ? (
                                            <ExpandMoreIcon fontSize="small" />
                                        ) : (
                                            <ChevronRightIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        sx={{ width: 110 }}
                                        defaultValue={row.key}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value !== row.key && e.target.value.trim()) {
                                                handlePatch(row.id, { key: e.target.value.trim() });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        sx={{ width: 130 }}
                                        defaultValue={row.display_name}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            if (e.target.value !== row.display_name && e.target.value.trim()) {
                                                handlePatch(row.id, { display_name: e.target.value.trim() });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {(row.line_items?.length ?? 0) > 0 ? (
                                        // Derived: sell price is the sum of the cost sheet.
                                        <Box sx={{ width: 110 }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                                {formatCurrency(row.sell_price)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: "var(--text-2)" }}>
                                                = cost sheet
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TextField
                                            size="small"
                                            type="number"
                                            sx={{ width: 110 }}
                                            defaultValue={row.sell_price}
                                            helperText={formatCurrency(row.sell_price)}
                                            disabled={busyId === row.id || !canEdit}
                                            onBlur={(e) => patchNumber(row, "sell_price", e.target.value, {}, e.target)}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        sx={{ width: 70 }}
                                        defaultValue={row.max_skus}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) =>
                                            patchNumber(row, "max_skus", e.target.value, { integer: true }, e.target)
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        sx={{ width: 70 }}
                                        defaultValue={row.term_months ?? ""}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) =>
                                            patchNumber(row, "term_months", e.target.value, {
                                                nullable: true,
                                                integer: true
                                            })
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        type="number"
                                        sx={{ width: 100 }}
                                        defaultValue={row.per_video_price ?? ""}
                                        helperText={
                                            row.per_video_price === null
                                                ? undefined
                                                : formatCurrency(row.per_video_price)
                                        }
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) =>
                                            patchNumber(row, "per_video_price", e.target.value, {
                                                nullable: true
                                            })
                                        }
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        sx={{ width: 170 }}
                                        defaultValue={row.payment_schedule ?? ""}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== (row.payment_schedule ?? "")) {
                                                handlePatch(row.id, { payment_schedule: v === "" ? null : v });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        multiline
                                        maxRows={3}
                                        sx={{ width: 220 }}
                                        defaultValue={row.deliverables ?? ""}
                                        disabled={busyId === row.id || !canEdit}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== (row.deliverables ?? "")) {
                                                handlePatch(row.id, { deliverables: v === "" ? null : v });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        size="small"
                                        checked={row.priority_scheduling}
                                        disabled={busyId === row.id || !canEdit}
                                        onChange={(e) =>
                                            handlePatch(row.id, { priority_scheduling: e.target.checked })
                                        }
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        size="small"
                                        checked={row.rollover}
                                        disabled={busyId === row.id || !canEdit}
                                        onChange={(e) => handlePatch(row.id, { rollover: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Checkbox
                                        size="small"
                                        checked={row.active}
                                        disabled={busyId === row.id || !canEdit}
                                        onChange={(e) => handlePatch(row.id, { active: e.target.checked })}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(row.id)}
                                            disabled={busyId === row.id}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell
                                    colSpan={13}
                                    sx={{ py: 0, border: expanded.has(row.id) ? undefined : 0 }}
                                >
                                    <Collapse in={expanded.has(row.id)} unmountOnExit>
                                        <Typography sx={{ fontWeight: 600, fontSize: 13, pt: 1.5 }}>
                                            Cost sheet — frozen onto the SOW when this package is
                                            attached
                                        </Typography>
                                        <LineItemsEditor
                                            items={row.line_items ?? []}
                                            pricingPackageId={row.id}
                                            canEdit={canEdit}
                                            onChanged={refetch}
                                        />
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                            </React.Fragment>
                        ))}
                        {canEdit && (
                        <TableRow>
                            <TableCell />
                            <TableCell>
                                <TextField
                                    size="small"
                                    sx={{ width: 110 }}
                                    placeholder="growth"
                                    value={draft.key}
                                    onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    sx={{ width: 130 }}
                                    placeholder="Growth Plan"
                                    value={draft.display_name}
                                    onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    sx={{ width: 110 }}
                                    placeholder="150000"
                                    value={draft.sell_price}
                                    onChange={(e) => setDraft({ ...draft, sell_price: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    sx={{ width: 70 }}
                                    placeholder="6"
                                    value={draft.max_skus}
                                    onChange={(e) => setDraft({ ...draft, max_skus: e.target.value })}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    type="number"
                                    sx={{ width: 70 }}
                                    placeholder="3"
                                    value={draft.term_months}
                                    onChange={(e) => setDraft({ ...draft, term_months: e.target.value })}
                                />
                            </TableCell>
                            <TableCell colSpan={6} />
                            <TableCell align="right">
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreate}
                                    disabled={!draft.key.trim() || !draft.display_name.trim()}
                                >
                                    Add
                                </Button>
                            </TableCell>
                        </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Packages;
